/**
 * Redux actions for publishing (to a social media, etc).
 * @module redux/actions/publish
 */

import { createAction } from "redux-actions";
import uuid from "uuid";
import Timecode from "../util/timecode";
import {
  getCategories as apiGetCategories,
  getFolders as apiGetFolders,
  getRenderMediaDestinations as apiGetRenderMediaDestinations
} from "../api/camio";
import {
  postImageToFacebook as apiPostImageToFacebook,
  postImageToTwitter as apiPostImageToTwitter,
  postVideoToFacebook as apiPostVideoToFacebook,
  postVideoToTwitter as apiPostVideoToTwitter,
  saveImageToCamio as apiSaveImageToCamio,
  saveVideoToCamio as apiSaveVideoToCamio,
  saveImageToDisk as apiSaveImageToDisk
} from "../api/publish";
import { actions as snackbarActions } from "../actions/snackbar";
import { submitJob as apiSubmitJob } from "../api/mediaEngine";
import SnackbarButtonTemplates from "../constants/snackbarButtonTemplates";

export const REQUEST_CATEGORIES = "PUBLISH/REQUEST_CATEGORIES";
export const RECEIVED_CATEGORIES = "PUBLISH/RECEIVED_CATEGORIES";
export const CATEGORIES_REQUEST_FAILED = "PUBLISH/CATEGORIES_REQUEST_FAILED";
export const REQUEST_FOLDERS = "PUBLISH/REQUEST_FOLDERS";
export const RECEIVED_FOLDERS = "PUBLISH/RECEIVED_FOLDERS";
export const FOLDERS_REQUEST_FAILED = "PUBLISH/FOLDERS_REQUEST_FAILED";
export const ASSET_TYPE_CHANGED = "PUBLISH/ASSET_TYPE_CHANGED";
export const CATEGORY_CHANGED = "PUBLISH/CATEGORY_CHANGED";
export const CONTEXT_CHANGED = "PUBLISH/CONTEXT_CHANGED";
export const FOLDER_CHANGED = "PUBLISH/FOLDER_CHANGED";
export const POST_TO_FB = "PUBLISH/POST_TO_FB";
export const POST_TO_TWITTER = "PUBLISH/POST_TO_TWITTER";
export const REQUEST_RENDER_MEDIA_DESTINATIONS =
  "PUBLISH/REQUEST_RENDER_MEDIA_DESTINATIONS";
export const RECEIVED_RENDER_MEDIA_DESTINATIONS =
  "PUBLISH/RECEIVED_RENDER_MEDIA_DESTINATIONS";
export const RENDER_MEDIA_DESTINATIONS_REQUEST_FAILED =
  "PUBLISH/RENDER_MEDIA_DESTINATIONS_REQUEST_FAILED";
export const RENDER_MEDIA_DESTINATION_CHANGED =
  "PUBLISH/RENDER_MEDIA_DESTINATION_CHANGED";
export const SAVE_TO_DISK = "PUBLISH/SAVE_TO_DISK";
export const WITH_PREVIEW_ONLY_IMAGES_CHANGED =
  "PUBLISH/WITH_PREVIEW_ONLY_IMAGES_CHANGED";

import ASSET_TYPE from "../constants/publishingAssetTypes";

export const initialize = assetType => dispatch => {
  dispatch(getCategories(assetType));
};

export const changeAssetType = assetType => dispatch => {
  dispatch(createAction(ASSET_TYPE_CHANGED)({ assetType }));
  dispatch(getCategories(assetType));
};

export const changeContext = contextId => dispatch => {
  dispatch(createAction(CONTEXT_CHANGED)({ contextId }));
  dispatch(getFolders(contextId));
};

export const changeFolder = folderId => dispatch =>
  dispatch(createAction(FOLDER_CHANGED)({ folderId }));

export const changeCategories = categoryIds => dispatch =>
  dispatch(createAction(CATEGORY_CHANGED)({ categoryIds }));

const requestFolders = createAction(REQUEST_FOLDERS);
const handleReceiveFolderList = folderList =>
  createAction(RECEIVED_FOLDERS)({ folderList });
const foldersRequestFailed = createAction(FOLDERS_REQUEST_FAILED);

const getFolders = contextId => dispatch => {
  dispatch(requestFolders());
  apiGetFolders(contextId)
    .then(folderList => dispatch(handleReceiveFolderList(folderList)))
    .catch(error => {
      console.log(error);
      return dispatch(foldersRequestFailed);
    });
};

const requestCategories = createAction(REQUEST_CATEGORIES);
const handleReceiveCategoryList = categoryList =>
  createAction(RECEIVED_CATEGORIES)({ categoryList });
const categoriesRequestFailed = createAction(CATEGORIES_REQUEST_FAILED);

export const getCategories = assetType => dispatch => {
  dispatch(requestCategories());
  let categoryAssetType = assetType;
  if (assetType.toLowerCase() === "movie") {
    categoryAssetType = "video"; // Movie is used for display purposes
  }
  apiGetCategories(categoryAssetType)
    .then(categoryList => dispatch(handleReceiveCategoryList(categoryList)))
    .catch(error => {
      console.log(error);
      return dispatch(categoriesRequestFailed());
    });
};

const getFormattedReplaceables = templateDefinition => {
  const { textReplaceables, mediaReplaceables } = templateDefinition;
  return (textReplaceables || []).concat(mediaReplaceables).map(r => ({
    id: r.id,
    name: r.name,
    provider: r.provider ? r.provider : "local",
    type: r.type,
    url: r.url,
    value: r.value
  }));
};

const generateRenderInputs = (asset, duration) => {
  const replaceables = getFormattedReplaceables(asset.templateDefinition);
  const templateId = asset.templateDefinition && asset.templateDefinition.uuid ? asset.templateDefinition.uuid : asset.asset.id;
  const templateData =  {
    replaceables,
    template: asset.asset.assetUrl,
    templateId
  };
  if (asset.primeStyle) {
    templateData.properties = [{ name: "style", value: asset.primeStyle}];
  }
  return [
    {
      startTime: 0,
      endTime: duration,
      templateData
    }
  ];
};

const generateVideoInput = clipperMovie => [
  {
    startTime: 0,
    endTime: clipperMovie.durationInMs / 1000,
    video: clipperMovie.fileUrl
  }
];

const generateComposeInputs = clipperMovie => {
  const timeMultiplier = clipperMovie.durationInMs / 1000000;
  return clipperMovie.timelineGraphics.map(graphic => ({
    startTime: graphic.start * timeMultiplier, // start time
    endTime: (graphic.end - graphic.start) * timeMultiplier, // duration (not end time)
    templateData: {
      templateId: graphic.templateId,
      replaceables: graphic.replaceables
    }
  }));
};

const generateMediaEngineParams = (state, title, description, duration) => {
  const { asset, clipperMovie, publish } = state;
  const data = {
    description,
    title
  };
  if (publish.assetType === ASSET_TYPE.image.toLowerCase()) {
    data.templateId = asset.templateDefinition.uuid || asset.asset.id;
    data.style = asset.primeStyle;
    data.replaceables = getFormattedReplaceables(asset.templateDefinition);
  } else if (clipperMovie.fileUrl) {
    data.videoInputs = generateVideoInput(clipperMovie);
    data.templateInputs = generateComposeInputs(clipperMovie);
  } else {
    data.templateInputs = generateRenderInputs(asset, duration);
  }

  return data;
};

export const postToFacebook = (
  facebookApi,
  title,
  description,
  duration,
  pageId
) => (dispatch, getState) => {
  const state = getState();
  const data = generateMediaEngineParams(state, title, description, duration);
  const headers = facebookApi.getHeader(pageId);
  data.apiUrl = facebookApi.apiUrl;
  data.headers = Object.keys(headers).map(k => ({
    name: k,
    value: headers[k]
  }));

  if (state.publish.assetType === ASSET_TYPE.image.toLowerCase()) {
    return apiPostImageToFacebook(facebookApi, data, pageId);
  }
  return apiPostVideoToFacebook(data, pageId);
};

export const postToTwitter = (twitterApi, title, description, duration) => (
  dispatch,
  getState
) => {
  const state = getState();
  const data = generateMediaEngineParams(state, title, description, duration);
  const headers = twitterApi.getHeader();
  data.apiUrl = twitterApi.apiUrl;
  data.headers = Object.keys(headers).map(k => ({
    name: k,
    value: headers[k]
  }));

  if (state.publish.assetType === ASSET_TYPE.image.toLowerCase()) {
    return apiPostImageToTwitter(twitterApi, data);
  }
  return apiPostVideoToTwitter(data);
};

export const saveToCamio = (title, duration) => (dispatch, getState) => {
  const state = getState();
  const uploadData = {
    categoryIds: state.publish.categoryIds,
    contextId: state.publish.contextId,
    folderId: state.publish.folderId
  };
  const assetData = generateMediaEngineParams(state, title, "", duration);
  if (state.publish.assetType === ASSET_TYPE.image.toLowerCase()) {
    return apiSaveImageToCamio(
      state.app.config.camio.ipAddress,
      uploadData,
      assetData
    );
  }
  return apiSaveVideoToCamio(
    state.app.config.camio.ipAddress,
    uploadData,
    assetData
  );
};

export const renderMedia = (title, duration, destination) => (
  dispatch,
  getState
) => {
  const state = getState();
  const dur = duration || 5;

  const assetData = generateMediaEngineParams(state, title, "", dur);

  if (state.publish.assetType === ASSET_TYPE.movie.toLowerCase()) {
    return apiSubmitJob({
      destinationPath: destination.path,
      preset: destination.preset,
      ...assetData
    }).then(response =>
      dispatch(
        snackbarActions.showSnack(uuid.v4(), {
          content: response.ok
            ? "Render job submitted successfully."
            : "Render job failed",
          timeout: 7000,
          buttonTemplate: SnackbarButtonTemplates.DISMISS
        })
      )
    );
  }
};

const requestRenderMediaDestinations = createAction(
  REQUEST_RENDER_MEDIA_DESTINATIONS
);
const handleReceiveRenderMediaDestinations = destinations =>
  createAction(RECEIVED_RENDER_MEDIA_DESTINATIONS)({ destinations });
const renderMediaDestinationsRequestFailed = createAction(
  RENDER_MEDIA_DESTINATIONS_REQUEST_FAILED
);

export const getRenderMediaDestinations = () => dispatch => {
  dispatch(requestRenderMediaDestinations());
  apiGetRenderMediaDestinations()
    .then(response => {
      const destinations = response.destinations.map(d => {
        const isValid = response.validPresetIds.some(id => d.preset === id);
        return {
          ...d,
          isValid
        };
      });
      return dispatch(handleReceiveRenderMediaDestinations(destinations));
    })
    .catch(error => {
      console.log(error);
      dispatch(renderMediaDestinationsRequestFailed());
    });
};

const handleChangeRenderMediaDestination = destinationId =>
  createAction(RENDER_MEDIA_DESTINATION_CHANGED)({ destinationId });

export const changeRenderMediaDestination = destinationId => dispatch =>
  dispatch(handleChangeRenderMediaDestination(destinationId));

export const changeWithPreviewOnlyImages = withPreviewOnlyImages =>
  createAction(WITH_PREVIEW_ONLY_IMAGES_CHANGED)({ withPreviewOnlyImages });

export const saveImageToDisk = title => (dispatch, getState) => {
  const state = getState();
  const assetData = generateMediaEngineParams(state, title);
  assetData.withPreviewOnlyImages = state.publish.withPreviewOnlyImages;
  apiSaveImageToDisk(assetData)
    .then(response => {
      if (response.previewImg) {
        return dispatch(
          snackbarActions.showSnack(uuid.v4(), {
            content: "Successfully saved image to disk",
            timeout: 7000,
            buttonTemplate: SnackbarButtonTemplates.DISMISS
          })
        );
      }

      return dispatch(
        snackbarActions.showSnack(uuid.v4(), {
          content: "Failed to save image to disk",
          timeout: 7000,
          buttonTemplate: SnackbarButtonTemplates.DISMISS
        })
      );
    })
    .catch(err => {
      console.log("Error while saving image to disk.");
      console.log(err);
      dispatch(
        snackbarActions.showSnack(uuid.v4(), {
          content: "Failed to save image to disk",
          timeout: 7000,
          buttonTemplate: SnackbarButtonTemplates.DISMISS
        })
      );
    });
};



// WEBPACK FOOTER //
// ./app/actions/publish.js